import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import useNumbersGame from '../../hooks/useNumbersGame';
import useAudio from '../../hooks/useAudio';

/**
 * Arabic Number Fishing Game ("Number Fishing")
 * ------------------------------------------------------------------
 * Listen / read / catch loop:
 *   1. A "Catch number" card shows a Western numeral + the Arabic word + a speaker.
 *   2. Fish swim by wearing EASTERN-ARABIC numerals (٠ ١ ٢ ٣ ...).
 *   3. The child taps the fish whose Arabic numeral matches the target.
 *      Correct → the boat's line reels it up (+points).  Wrong → it darts away.
 *
 * Data:  numbersData from useNumbersGame (Firestore `numbers` collection).
 * Audio: useAudio for SFX; number pronunciation plays each number's audioURL.
 *
 * NOT YET WIRED (Phase 2, see notes at finishRound):
 *   - Persistent best scores. useNumbersGame.saveLevelData writes to the
 *     SHARED `arabicNumberSpeed` progress bucket, so calling it here would
 *     clobber Raqam Rush. A dedicated useFishingGame hook (own storage key)
 *     should own persistence + analytics. v1 ships without saving progress.
 * ------------------------------------------------------------------
 */

// Fallback list (0–20) used only if Firestore numbersData hasn't loaded.
const FALLBACK_NUMBERS = [
  { n: 0, ar: '٠', word: 'صفر', pron: 'Sifr', audioURL: '' },
  { n: 1, ar: '١', word: 'واحد', pron: 'Wāḥid', audioURL: '' },
  { n: 2, ar: '٢', word: 'اثنان', pron: 'Ithnān', audioURL: '' },
  { n: 3, ar: '٣', word: 'ثلاثة', pron: 'Thalātha', audioURL: '' },
  { n: 4, ar: '٤', word: 'أربعة', pron: 'Arbaʿa', audioURL: '' },
  { n: 5, ar: '٥', word: 'خمسة', pron: 'Khamsa', audioURL: '' },
  { n: 6, ar: '٦', word: 'ستة', pron: 'Sitta', audioURL: '' },
  { n: 7, ar: '٧', word: 'سبعة', pron: 'Sabʿa', audioURL: '' },
  { n: 8, ar: '٨', word: 'ثمانية', pron: 'Thamāniya', audioURL: '' },
  { n: 9, ar: '٩', word: 'تسعة', pron: 'Tisʿa', audioURL: '' },
  { n: 10, ar: '١٠', word: 'عشرة', pron: 'ʿAshara', audioURL: '' },
];

const LEVELS = {
  1: { range: [0, 10], goal: 8, time: 70, density: 4, speed: 1.0, title: 'Tide pool', sub: 'Numbers 0–10' },
  2: { range: [0, 20], goal: 10, time: 80, density: 5, speed: 1.18, title: 'Open water', sub: 'Numbers 0–20' },
  3: { range: [0, 50], goal: 12, time: 90, density: 6, speed: 1.32, title: 'Reef edge', sub: 'Numbers 0–50' },
  4: { range: [0, 100], goal: 14, time: 100, density: 6, speed: 1.45, title: 'Open sea', sub: 'Numbers 0–100' },
  5: { range: [0, 200], goal: 16, time: 110, density: 7, speed: 1.6, title: 'The deep', sub: 'Numbers 0–200' },
};
const MAX_LEVEL = 5;

// Bright, kid-friendly fish palettes (body / shade / belly highlight).
const FISH_COLORS = [
  { body: '#ff8a5c', dark: '#e85d34', belly: '#ffd9b8' },
  { body: '#3fc7c0', dark: '#1d9b94', belly: '#bff2ef' },
  { body: '#ffd24d', dark: '#e8a92b', belly: '#fff1c4' },
  { body: '#b483dd', dark: '#8a4fc0', belly: '#ecd9f7' },
  { body: '#ff6f91', dark: '#e84d72', belly: '#ffd2dd' },
  { body: '#6fa8ff', dark: '#3f78e0', belly: '#d3e2ff' },
];

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[(Math.random() * arr.length) | 0];
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

// Adaptive difficulty ("flow"): intensity 0→1 rides the player's streak and
// drops on misses. It nudges swim speed and crowd size up for kids who are
// cruising, and eases back for kids who are struggling — without changing the
// number range. Tune freely; set enabled:false to get the old flat difficulty.
const ADAPT = {
  enabled: true,
  streakForMax: 6,  // streak at which intensity reaches 1.0
  speedBoost: 0.3,  // up to +30% swim speed at full intensity
  extraFish: 2,     // up to +2 fish on screen at full intensity
  missDrop: 0.45,   // intensity lost on a wrong tap
  ease: 1.6,        // how quickly intensity follows its target
};

export default function ArabicNumberFishingGame() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const audioCtxRef = useRef(null);
  const numAudioRef = useRef(null); // plays each number's pronunciation (audioURL)

  // App data + sound effects
  const { numbersData, isLoading } = useNumbersGame();
  const { playCorrectSound, playIncorrectSound, AudioElements } = useAudio();

  // Map Firestore numbers → the shape this game uses. Falls back until loaded.
  const NUMBERS = useMemo(() => {
    if (Array.isArray(numbersData) && numbersData.length) {
      return numbersData
        .map((d) => ({
          n: d.englishNumeral,
          ar: d.arabicNumeral || String(d.englishNumeral),
          word: d.arabicWord || '',
          pron: d.pronunciation || '',
          audioURL: d.audioURL || '',
        }))
        .sort((a, b) => a.n - b.n);
    }
    return FALLBACK_NUMBERS;
  }, [numbersData]);
  const numbersRef = useRef(NUMBERS);
  useEffect(() => { numbersRef.current = NUMBERS; }, [NUMBERS]);
  const dataReady = !isLoading && Array.isArray(numbersData) && numbersData.length > 0;

  // Mutable game world (never triggers re-render)
  const G = useRef({
    fish: [], bubbles: [], particles: [], floaters: [], seaweed: [],
    hook: null, shake: 0, t: 0, last: 0, spawnT: 0, noTargetT: 0,
    W: 0, H: 0, dpr: 1, reduced: false, nextId: 1, lastSec: 0, raysPhase: 0, intensity: 0,
  });

  // React state (HUD / flow) + refs that mirror what the loop needs
  const [phase, setPhase] = useState('menu'); // menu | playing | complete
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [caught, setCaught] = useState(0);
  const [goal, setGoal] = useState(8);
  const [timeLeft, setTimeLeft] = useState(70);
  const [target, setTarget] = useState(FALLBACK_NUMBERS[3]);
  const [muted, setMuted] = useState(false);
  const [result, setResult] = useState({ stars: 0, accuracy: 0 });
  const [compact, setCompact] = useState(false);   // narrow viewport (phones)
  const [portrait, setPortrait] = useState(false); // taller than wide
  const [rotateDismissed, setRotateDismissed] = useState(false);

  const phaseRef = useRef(phase);
  const targetRef = useRef(target);
  const mutedRef = useRef(muted);
  const levelRef = useRef(level);
  const tapStats = useRef({ correct: 0, total: 0 });
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { targetRef.current = target; }, [target]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { levelRef.current = level; }, [level]);

  // ── Audio ─────────────────────────────────────────────────────────
  // SFX come from useAudio (playCorrectSound / playIncorrectSound).
  // Number pronunciation plays the number's audioURL; a soft synth chime
  // is the fallback when a recording is missing.
  const ac = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };
  const chime = () => {
    const ctx = ac(); if (!ctx || mutedRef.current) return;
    [[660, 0], [880, 0.12]].forEach(([freq, t0]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.setValueAtTime(freq, ctx.currentTime + t0);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t0);
      g.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t0 + 0.18);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + t0); o.stop(ctx.currentTime + t0 + 0.2);
    });
  };
  const speakNumber = useCallback((num) => {
    if (mutedRef.current || !num) return;
    if (num.audioURL) {
      try {
        if (!numAudioRef.current) numAudioRef.current = new Audio();
        const a = numAudioRef.current;
        a.src = num.audioURL; a.currentTime = 0;
        const p = a.play();
        if (p) p.catch(() => chime());
        return;
      } catch (e) { /* fall through */ }
    }
    chime();
  }, []);

  // ── World helpers ─────────────────────────────────────────────────
  const levelRangeNumbers = useCallback(() => {
    const [lo, hi] = LEVELS[levelRef.current].range;
    const pool = numbersRef.current.filter((x) => x.n >= lo && x.n <= hi);
    return pool.length ? pool : numbersRef.current;
  }, []);

  const makeFish = useCallback((forced) => {
    const g = G.current; const pool = levelRangeNumbers();
    const num = forced || pick(pool);
    const dir = Math.random() < 0.5 ? 1 : -1;
    const touch = g.touch, narrow = g.W < 560;
    const z = rand(touch ? 0.9 : 0.72, touch ? 1.18 : 1.12); // larger tap targets on touch
    const lv = LEVELS[levelRef.current];
    const topBand = narrow ? 0.36 : 0.26; // keep fish below the HUD card on phones
    return {
      id: g.nextId++, num, dir, z,
      x: dir === 1 ? -90 : g.W + 90,
      y: rand(g.H * topBand, g.H * 0.86),
      speed: rand(34, 52) * z * lv.speed * (touch ? 0.9 : 1),
      bobAmp: rand(6, 16), bobFreq: rand(0.6, 1.2), phase: rand(0, Math.PI * 2),
      tail: rand(0, Math.PI * 2), col: pick(FISH_COLORS),
      state: 'swim', vy: 0, hookT: 0,
    };
  }, [levelRangeNumbers]);

  const hasTargetFish = () => {
    const tn = targetRef.current.n;
    return G.current.fish.some((f) => f.state === 'swim' && f.num.n === tn);
  };

  const chooseTarget = useCallback(() => {
    const pool = levelRangeNumbers();
    const next = pick(pool);
    targetRef.current = next;
    setTarget(next);
    // guarantee a fish carrying it
    if (!G.current.fish.some((f) => f.state === 'swim' && f.num.n === next.n)) {
      G.current.fish.push(makeFish(next));
    }
    setTimeout(() => speakNumber(next), 220);
  }, [levelRangeNumbers, makeFish, speakNumber]);

  const burst = (x, y, color, count = 14) => {
    const g = G.current;
    for (let i = 0; i < count; i++) {
      const a = rand(0, Math.PI * 2); const sp = rand(40, 160);
      g.particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 30, life: 1, r: rand(2, 5), color });
    }
  };

  // ── Resize / DPR ──────────────────────────────────────────────────
  const resize = useCallback(() => {
    const wrap = wrapRef.current, cv = canvasRef.current; if (!wrap || !cv) return;
    const r = wrap.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    G.current.W = r.width; G.current.H = r.height; G.current.dpr = dpr;
    setCompact(r.width < 560);
    setPortrait(r.height > r.width * 1.05);
    cv.width = Math.round(r.width * dpr); cv.height = Math.round(r.height * dpr);
    cv.style.width = r.width + 'px'; cv.style.height = r.height + 'px';
    const ctx = cv.getContext('2d'); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // seaweed anchored to floor
    if (G.current.seaweed.length === 0) {
      for (let i = 0; i < 7; i++) {
        G.current.seaweed.push({ x: rand(0.05, 0.95), h: rand(70, 150), w: rand(8, 16), phase: rand(0, 6), hue: pick(['#1f7a5a', '#247a4e', '#2f8c5e']) });
      }
    }
  }, []);

  // ── Update ────────────────────────────────────────────────────────
  const update = useCallback((dt) => {
    const g = G.current; const lv = LEVELS[levelRef.current];
    g.t += dt; g.raysPhase += dt * 0.25;
    if (g.shake > 0) g.shake = Math.max(0, g.shake - dt * 4);

    // round timer (drive React once per second)
    const secLeft = Math.max(0, Math.ceil(lv.time - g.t));
    if (secLeft !== g.lastSec) { g.lastSec = secLeft; setTimeLeft(secLeft); if (secLeft <= 0) finishRound(false); }

    // adaptive intensity rides the streak, eases on misses
    if (ADAPT.enabled) {
      const targetI = clamp(streakRef.current / ADAPT.streakForMax, 0, 1);
      g.intensity += (targetI - g.intensity) * Math.min(1, dt * ADAPT.ease);
    } else g.intensity = 0;
    const speedF = 1 + g.intensity * ADAPT.speedBoost * (g.touch ? 0.7 : 1);
    const dens = lv.density + Math.round(g.intensity * ADAPT.extraFish);

    // spawn to maintain (adaptive) density
    g.spawnT -= dt;
    const active = g.fish.filter((f) => f.state === 'swim').length;
    if (active < dens && g.spawnT <= 0) { g.fish.push(makeFish()); g.spawnT = rand(0.4, 1.1); }

    // guarantee target presence
    if (!hasTargetFish()) { g.noTargetT += dt; if (g.noTargetT > 0.7) { g.fish.push(makeFish(targetRef.current)); g.noTargetT = 0; } }
    else g.noTargetT = 0;

    // fish motion
    for (const f of g.fish) {
      if (f.state === 'swim') {
        f.x += f.dir * f.speed * speedF * dt;
        f.y += Math.sin(g.t * f.bobFreq + f.phase) * f.bobAmp * dt;
        f.tail += dt * 8;
      } else if (f.state === 'flee') {
        f.x += f.dir * f.speed * 3 * dt; f.tail += dt * 22;
      } else if (f.state === 'hooked') {
        const hk = g.hook;
        f.hookT = Math.min(1, f.hookT + dt / 0.75);
        const e = easeInOut(f.hookT);
        f.x = hk.sx + (hk.bx - hk.sx) * e;
        f.y = hk.sy + (hk.by - hk.sy) * e;
        f.tail += dt * 26;
        if (f.hookT >= 1) {
          burst(hk.bx, hk.by - 6, f.col.body, 18);
          g.floaters.push({ x: hk.bx, y: hk.by - 18, text: '+' + g.lastPts, life: 1, color: '#ffe07a' });
          f.state = 'gone'; g.hook = null;
          setTimeout(() => { if (phaseRef.current === 'playing') chooseTarget(); }, 260);
        }
      }
    }
    g.fish = g.fish.filter((f) => f.state !== 'gone' && f.x > -140 && f.x < g.W + 140);

    // bubbles
    if (Math.random() < (g.reduced ? 0.15 : 0.45)) g.bubbles.push({ x: rand(0, g.W), y: g.H + 10, r: rand(2, 6), sp: rand(20, 55), wob: rand(0, 6) });
    for (const b of g.bubbles) { b.y -= b.sp * dt; b.x += Math.sin(g.t * 2 + b.wob) * 6 * dt; }
    g.bubbles = g.bubbles.filter((b) => b.y > -10);

    // particles
    for (const p of g.particles) { p.vy += 240 * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.life -= dt * 1.4; }
    g.particles = g.particles.filter((p) => p.life > 0);

    // floaters
    for (const fl of g.floaters) { fl.y -= 36 * dt; fl.life -= dt * 0.9; }
    g.floaters = g.floaters.filter((fl) => fl.life > 0);
  }, [makeFish, chooseTarget]);

  // ── Draw ──────────────────────────────────────────────────────────
  const drawFish = (ctx, f) => {
    const s = 30 * f.z; // body half-length
    const wig = Math.sin(f.tail) * 0.5;
    ctx.save();
    ctx.translate(f.x, f.y);
    ctx.scale(f.dir, 1);
    ctx.globalAlpha = f.state === 'flee' ? 0.85 : 1;

    // soft shadow
    ctx.save(); ctx.globalAlpha *= 0.18; ctx.fillStyle = '#001b2e';
    ctx.beginPath(); ctx.ellipse(0, s * 0.95, s * 1.05, s * 0.42, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();

    // tail
    ctx.fillStyle = f.col.dark;
    ctx.beginPath();
    ctx.moveTo(-s * 0.7, 0);
    ctx.quadraticCurveTo(-s * 1.5, -s * (0.55 + wig), -s * 1.55, -s * (0.2 + wig));
    ctx.quadraticCurveTo(-s * 1.2, 0, -s * 1.55, s * (0.2 - wig));
    ctx.quadraticCurveTo(-s * 1.5, s * (0.55 - wig), -s * 0.7, 0);
    ctx.fill();

    // dorsal + pelvic fins
    ctx.fillStyle = f.col.dark;
    ctx.beginPath(); ctx.moveTo(-s * 0.1, -s * 0.7); ctx.quadraticCurveTo(s * 0.2, -s * 1.15, s * 0.55, -s * 0.55); ctx.quadraticCurveTo(s * 0.1, -s * 0.6, -s * 0.1, -s * 0.7); ctx.fill();
    ctx.beginPath(); ctx.moveTo(-s * 0.05, s * 0.6); ctx.quadraticCurveTo(s * 0.25, s * 1.0, s * 0.5, s * 0.55); ctx.quadraticCurveTo(s * 0.15, s * 0.55, -s * 0.05, s * 0.6); ctx.fill();

    // body
    const grad = ctx.createLinearGradient(0, -s, 0, s);
    grad.addColorStop(0, f.col.body); grad.addColorStop(0.62, f.col.body); grad.addColorStop(1, f.col.belly);
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.ellipse(0, 0, s * 1.18, s * 0.82, 0, 0, Math.PI * 2); ctx.fill();
    // rim light
    ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.ellipse(-s * 0.1, -s * 0.18, s * 0.95, s * 0.5, 0, Math.PI * 1.1, Math.PI * 1.9); ctx.stroke();

    // side fin
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.beginPath(); ctx.moveTo(s * 0.2, s * 0.05); ctx.quadraticCurveTo(s * 0.0, s * 0.6, s * 0.55, s * 0.45); ctx.quadraticCurveTo(s * 0.45, s * 0.2, s * 0.2, s * 0.05); ctx.fill();

    // number badge — pill auto-fits 1–3 digits; fish size stays constant
    const by = -s * 0.02, br = s * 0.62, bx = -s * 0.05;
    ctx.scale(f.dir, 1); // un-mirror so digits read correctly
    const digits = f.num.ar;
    let fs = br * 1.25;
    ctx.font = `700 ${fs}px Cairo, 'Segoe UI', Tahoma, sans-serif`;
    let tw = ctx.measureText(digits).width;
    const maxInner = br * 2.1;
    if (tw > maxInner) { fs *= maxInner / tw; ctx.font = `700 ${fs}px Cairo, 'Segoe UI', Tahoma, sans-serif`; tw = ctx.measureText(digits).width; }
    const px = bx * f.dir;
    const pillW = Math.max(br * 2, tw + fs * 0.7);
    const pillH = br * 1.9, rr = pillH / 2;
    const x0 = px - pillW / 2, y0 = by - pillH / 2;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.beginPath();
    ctx.moveTo(x0 + rr, y0);
    ctx.arcTo(x0 + pillW, y0, x0 + pillW, y0 + pillH, rr);
    ctx.arcTo(x0 + pillW, y0 + pillH, x0, y0 + pillH, rr);
    ctx.arcTo(x0, y0 + pillH, x0, y0, rr);
    ctx.arcTo(x0, y0, x0 + pillW, y0, rr);
    ctx.closePath(); ctx.fill();
    ctx.strokeStyle = 'rgba(0,40,60,0.12)'; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.fillStyle = '#0c3a4a';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(digits, px, by + 1);
    ctx.scale(f.dir, 1);

    // eye
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(s * 0.78, -s * 0.18, s * 0.2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#10242e'; ctx.beginPath(); ctx.arc(s * 0.83, -s * 0.18, s * 0.1, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(s * 0.8, -s * 0.23, s * 0.04, 0, Math.PI * 2); ctx.fill();

    ctx.restore();
  };

  const draw = useCallback(() => {
    const g = G.current; const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext('2d'); const W = g.W, H = g.H;
    ctx.save();
    if (g.shake > 0) ctx.translate(rand(-1, 1) * g.shake * 5, rand(-1, 1) * g.shake * 5);

    // water column
    const water = ctx.createLinearGradient(0, 0, 0, H);
    water.addColorStop(0, '#4fd3d6'); water.addColorStop(0.28, '#1fa3c4');
    water.addColorStop(0.6, '#0e6f9e'); water.addColorStop(1, '#062f4e');
    ctx.fillStyle = water; ctx.fillRect(0, 0, W, H);

    // god rays
    ctx.save(); ctx.globalCompositeOperation = 'screen';
    for (let i = 0; i < 5; i++) {
      const cx = (i / 4) * W + Math.sin(g.raysPhase + i) * 30;
      const a = 0.05 + 0.03 * Math.sin(g.raysPhase * 1.3 + i * 2);
      const rg = ctx.createLinearGradient(cx, 0, cx + 60, H);
      rg.addColorStop(0, `rgba(255,240,190,${a})`); rg.addColorStop(1, 'rgba(255,240,190,0)');
      ctx.fillStyle = rg;
      ctx.beginPath(); ctx.moveTo(cx - 26, 0); ctx.lineTo(cx + 26, 0); ctx.lineTo(cx + 120, H); ctx.lineTo(cx + 30, H); ctx.closePath(); ctx.fill();
    }
    ctx.restore();

    // sand floor + caustic shimmer
    const sandY = H * 0.9;
    const sand = ctx.createLinearGradient(0, sandY, 0, H);
    sand.addColorStop(0, '#1c5e7a'); sand.addColorStop(1, '#caa86a');
    ctx.fillStyle = sand;
    ctx.beginPath(); ctx.moveTo(0, sandY + 8);
    for (let x = 0; x <= W; x += 40) ctx.lineTo(x, sandY + Math.sin(x * 0.03 + g.t) * 6);
    ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath(); ctx.fill();
    ctx.save(); ctx.globalAlpha = 0.12; ctx.strokeStyle = '#fff7d8'; ctx.lineWidth = 2;
    for (let x = 0; x < W; x += 26) { const off = Math.sin(g.t * 1.5 + x * 0.05) * 5; ctx.beginPath(); ctx.moveTo(x + off, sandY + 14); ctx.lineTo(x + 12 + off, H); ctx.stroke(); }
    ctx.restore();

    // seaweed
    for (const w of g.seaweed) {
      const bx = w.x * W; ctx.strokeStyle = w.hue; ctx.lineWidth = w.w; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(bx, H);
      for (let s = 0; s <= 1; s += 0.25) { const yy = H - w.h * s; const xx = bx + Math.sin(g.t * 1.4 + w.phase + s * 3) * 14 * s; ctx.lineTo(xx, yy); }
      ctx.stroke();
    }

    // bubbles
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    for (const b of g.bubbles) { ctx.globalAlpha = 0.4; ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2); ctx.fill(); }
    ctx.globalAlpha = 1;

    // fish (back to front)
    const sorted = [...g.fish].sort((a, b) => a.z - b.z);
    for (const f of sorted) drawFish(ctx, f);

    // ── Surface band + illustrated boat ──────────────────────────
    const bob = Math.sin(g.t * 1.6) * 3;
    const narrow = W < 560;
    const bs = narrow ? 0.78 : 1;            // smaller boat on phones
    const boatX = narrow ? clamp(W * 0.2, 76, 120) : clamp(W * 0.22, 130, 185);
    const boatY = 30 + bob;

    // bright surface strip with wave crest
    ctx.save(); ctx.globalCompositeOperation = 'screen';
    const band = ctx.createLinearGradient(0, 0, 0, 46);
    band.addColorStop(0, 'rgba(196,246,255,0.34)'); band.addColorStop(1, 'rgba(196,246,255,0)');
    ctx.fillStyle = band;
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(W, 0); ctx.lineTo(W, 26);
    for (let x = W; x >= 0; x -= 22) ctx.lineTo(x, 26 + Math.sin(x * 0.05 + g.t * 2) * 4);
    ctx.closePath(); ctx.fill(); ctx.restore();

    // soft shadow on the water
    ctx.save(); ctx.globalAlpha = 0.16; ctx.fillStyle = '#02283f';
    ctx.beginPath(); ctx.ellipse(boatX, boatY + 30 * bs, 62 * bs, 9 * bs, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();

    // hull silhouette (reused for fill, clip, and outline)
    const bw = 58 * bs, bd = 22 * bs;
    const hullPath = () => {
      ctx.beginPath();
      ctx.moveTo(boatX - bw, boatY);
      ctx.quadraticCurveTo(boatX, boatY + bd * 1.85, boatX + bw, boatY);
      ctx.quadraticCurveTo(boatX + bw * 0.8, boatY - 9, boatX + bw * 0.6, boatY - 4);
      ctx.quadraticCurveTo(boatX, boatY + 5, boatX - bw * 0.6, boatY - 4);
      ctx.quadraticCurveTo(boatX - bw * 0.8, boatY - 9, boatX - bw, boatY);
      ctx.closePath();
    };
    hullPath(); ctx.fillStyle = '#a86a38'; ctx.fill();
    // interior shade + plank lines (clipped to hull)
    ctx.save(); hullPath(); ctx.clip();
    ctx.fillStyle = '#7d4a24';
    ctx.beginPath(); ctx.ellipse(boatX, boatY + bd + 10, bw, bd + 4, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(58,28,12,0.4)'; ctx.lineWidth = 1.4;
    for (let i = 1; i <= 2; i++) { const yy = boatY + 4 + i * 7; ctx.beginPath(); ctx.moveTo(boatX - bw * 0.9, yy); ctx.quadraticCurveTo(boatX, yy + 9, boatX + bw * 0.9, yy); ctx.stroke(); }
    ctx.restore();
    // red trim stripe along the deck
    ctx.strokeStyle = '#e0563a'; ctx.lineWidth = 5; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(boatX - bw * 0.6, boatY - 4); ctx.quadraticCurveTo(boatX, boatY + 5, boatX + bw * 0.6, boatY - 4); ctx.stroke();
    // crisp outline (the "graphic" look)
    hullPath(); ctx.strokeStyle = '#3a2415'; ctx.lineWidth = 2.5; ctx.lineJoin = 'round'; ctx.stroke();
    // gunwale highlight
    ctx.strokeStyle = 'rgba(255,228,194,0.6)'; ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.moveTo(boatX - bw * 0.58, boatY - 5); ctx.quadraticCurveTo(boatX, boatY + 2, boatX + bw * 0.58, boatY - 5); ctx.stroke();

    // reel
    const reelX = boatX + 14 * bs, reelY = boatY - 6 * bs;
    ctx.fillStyle = '#3a536c'; ctx.beginPath(); ctx.arc(reelX, reelY, 6 * bs, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#1f3346'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = '#d3dde6'; ctx.beginPath(); ctx.arc(reelX, reelY, 2.2 * bs, 0, Math.PI * 2); ctx.fill();

    // tapered rod arcing out over the water
    const tipX = boatX + 100 * bs, tipY = boatY - 14 * bs + bob * 0.4;
    const rcx = boatX + 62 * bs, rcy = boatY - 22 * bs;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#5a3a1e'; ctx.lineWidth = 4.5 * bs;
    ctx.beginPath(); ctx.moveTo(reelX, reelY - 2 * bs); ctx.quadraticCurveTo(rcx, rcy, tipX, tipY); ctx.stroke();
    ctx.strokeStyle = '#9a6635'; ctx.lineWidth = 1.8 * bs;
    ctx.beginPath(); ctx.moveTo(reelX, reelY - 2 * bs); ctx.quadraticCurveTo(rcx, rcy, tipX, tipY); ctx.stroke();
    ctx.fillStyle = '#2a1a0e';
    [0.45, 0.78].forEach((tt) => {
      const qx = (1 - tt) * (1 - tt) * reelX + 2 * (1 - tt) * tt * rcx + tt * tt * tipX;
      const qy = (1 - tt) * (1 - tt) * (reelY - 2 * bs) + 2 * (1 - tt) * tt * rcy + tt * tt * tipY;
      ctx.beginPath(); ctx.arc(qx, qy, 2 * bs, 0, Math.PI * 2); ctx.fill();
    });
    g.rodTip = { x: tipX, y: tipY };

    // line: reeling a catch, or an idle bobbing lure
    ctx.strokeStyle = 'rgba(255,255,255,0.62)'; ctx.lineWidth = 1.3;
    if (g.hook) {
      ctx.beginPath(); ctx.moveTo(g.hook.bx, g.hook.by); ctx.lineTo(g.hook.fx, g.hook.fy); ctx.stroke();
    } else {
      const sway = Math.sin(g.t * 1.8) * 10 * bs;
      const hx = tipX + sway, hy = tipY + 82 * bs + Math.sin(g.t * 2) * 3;
      ctx.beginPath(); ctx.moveTo(tipX, tipY); ctx.quadraticCurveTo(tipX + sway * 0.5, tipY + 42 * bs, hx, hy); ctx.stroke();
      const lr = 5 * bs;
      ctx.fillStyle = '#ff5d5d'; ctx.beginPath(); ctx.arc(hx, hy, lr, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(hx, hy, lr, Math.PI * 0.9, Math.PI * 1.6); ctx.fill();
      ctx.strokeStyle = '#b23232'; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(hx, hy, lr, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = '#cfd6db'; ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.moveTo(hx, hy + lr); ctx.quadraticCurveTo(hx + lr, hy + 2.4 * lr, hx - 0.2 * lr, hy + 2.4 * lr); ctx.stroke();
    }

    // surface shimmer line
    ctx.save(); ctx.globalCompositeOperation = 'screen'; ctx.strokeStyle = 'rgba(255,255,255,0.25)'; ctx.lineWidth = 2;
    ctx.beginPath(); for (let x = 0; x <= W; x += 16) { const yy = 6 + Math.sin(x * 0.05 + g.t * 2) * 3; x === 0 ? ctx.moveTo(x, yy) : ctx.lineTo(x, yy); } ctx.stroke(); ctx.restore();

    // particles
    for (const p of g.particles) { ctx.globalAlpha = clamp(p.life, 0, 1); ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill(); }
    ctx.globalAlpha = 1;

    // floaters
    for (const fl of g.floaters) { ctx.globalAlpha = clamp(fl.life, 0, 1); ctx.fillStyle = fl.color; ctx.font = "700 22px Fredoka, system-ui, sans-serif"; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(fl.text, fl.x, fl.y); }
    ctx.globalAlpha = 1;

    ctx.restore();
  }, []);

  // ── Loop ──────────────────────────────────────────────────────────
  const loop = useCallback((ts) => {
    const g = G.current;
    if (!g.last) g.last = ts;
    let dt = (ts - g.last) / 1000; g.last = ts; dt = Math.min(dt, 0.05);
    if (phaseRef.current === 'playing') update(dt);
    draw();
    rafRef.current = requestAnimationFrame(loop);
  }, [update, draw]);

  // ── Tap handling ──────────────────────────────────────────────────
  const onPointerDown = useCallback((e) => {
    if (phaseRef.current !== 'playing') return;
    const g = G.current; const cv = canvasRef.current;
    const r = cv.getBoundingClientRect();
    const px = (e.clientX ?? (e.touches && e.touches[0].clientX)) - r.left;
    const py = (e.clientY ?? (e.touches && e.touches[0].clientY)) - r.top;

    // topmost (largest z, drawn last) fish under the point
    const candidates = g.fish.filter((f) => f.state === 'swim');
    candidates.sort((a, b) => b.z - a.z);
    let hit = null;
    const tol = g.touch ? 1.4 : 1.15;
    for (const f of candidates) {
      const rx = 38 * f.z, ry = 30 * f.z;
      const dx = (px - f.x) / rx, dy = (py - f.y) / ry;
      if (dx * dx + dy * dy <= tol) { hit = f; break; }
    }
    if (!hit) return;

    tapStats.current.total += 1;
    if (hit.num.n === targetRef.current.n) {
      // correct
      tapStats.current.correct += 1;
      const lv = LEVELS[levelRef.current];
      const pts = 50 + Math.min(streakRef.current, 8) * 10;
      g.lastPts = pts;
      hit.state = 'hooked'; hit.hookT = 0;
      const rt = g.rodTip || { x: g.W * 0.5 + 100, y: 18 };
      g.hook = { bx: rt.x, by: rt.y, sx: hit.x, sy: hit.y, fx: hit.x, fy: hit.y };
      // keep line endpoint glued to fish during reel
      const glue = () => { if (g.hook && hit.state === 'hooked') { g.hook.fx = hit.x; g.hook.fy = hit.y; requestAnimationFrame(glue); } };
      requestAnimationFrame(glue);
      playCorrectSound();
      setScore((s) => s + pts);
      setStreak((s) => { const ns = s + 1; streakRef.current = ns; setBestStreak((b) => Math.max(b, ns)); return ns; });
      setCaught((c) => {
        const nc = c + 1;
        if (nc >= LEVELS[levelRef.current].goal) setTimeout(() => finishRound(true), 700);
        return nc;
      });
    } else {
      // wrong
      hit.state = 'flee'; g.shake = 0.6; playIncorrectSound();
      g.intensity = Math.max(0, g.intensity - ADAPT.missDrop);
      setStreak(0); streakRef.current = 0;
      burst(hit.x, hit.y, '#ff5252', 8);
    }
  }, []);

  const streakRef = useRef(0);
  useEffect(() => { streakRef.current = streak; }, [streak]);

  // ── Flow ──────────────────────────────────────────────────────────
  const finishRound = useCallback((won) => {
    if (phaseRef.current !== 'playing') return;
    const t = tapStats.current;
    const acc = t.total ? t.correct / t.total : 0;
    let stars = 1;
    if (won) stars = acc >= 0.9 ? 3 : acc >= 0.72 ? 2 : 1;
    setResult({ stars, accuracy: Math.round(acc * 100) });
    setPhase('complete'); phaseRef.current = 'complete';
    try { if (numAudioRef.current) numAudioRef.current.pause(); } catch (e) {}
    // PHASE 2 — persist progress here once a dedicated useFishingGame hook
    // exists (own storage key, so it doesn't collide with Raqam Rush):
    //   saveFishingLevel(level, { totalScore: score, stars, accuracy: Math.round(acc*100), caught, bestStreak });
    //   saveSessionData({ game: 'arabicNumberFishing', score, stars, ... });
  }, [score, caught, bestStreak]);

  const startLevel = useCallback((lv) => {
    const g = G.current;
    levelRef.current = lv; setLevel(lv);
    const conf = LEVELS[lv];
    g.fish = []; g.bubbles = []; g.particles = []; g.floaters = []; g.hook = null;
    g.t = 0; g.last = 0; g.spawnT = 0; g.noTargetT = 0; g.lastSec = conf.time; g.shake = 0; g.intensity = 0;
    tapStats.current = { correct: 0, total: 0 };
    setScore(0); setStreak(0); streakRef.current = 0; setBestStreak(0);
    setCaught(0); setGoal(conf.goal); setTimeLeft(conf.time);
    // seed a few fish so the reef isn't empty on entry
    for (let i = 0; i < conf.density; i++) { const f = makeFish(); f.x = rand(60, g.W - 60); g.fish.push(f); }
    try { const c = ac(); if (c && c.state === 'suspended') c.resume(); } catch (e) {}
    setPhase('playing'); phaseRef.current = 'playing';
    chooseTarget();
  }, [makeFish, chooseTarget]);

  // ── Mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    G.current.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    G.current.touch = ('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0;
    resize();
    const ro = new ResizeObserver(resize); if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', resize);
    rafRef.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(rafRef.current); ro.disconnect(); window.removeEventListener('resize', resize); try { if (numAudioRef.current) numAudioRef.current.pause(); } catch (e) {} };
  }, [loop, resize]);

  // ── UI ────────────────────────────────────────────────────────────
  const Stars = ({ n }) => (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{ fontSize: 44, filter: i < n ? 'none' : 'grayscale(1) opacity(0.35)', transform: i < n ? 'scale(1)' : 'scale(0.86)', transition: 'all .3s' }}>⭐</span>
      ))}
    </div>
  );

  const timePct = Math.max(0, timeLeft / LEVELS[level].time);

  return (
    <div ref={wrapRef} style={S.wrap}>
      <style>{CSS}</style>
      <AudioElements />
      <canvas ref={canvasRef} style={S.canvas} onPointerDown={onPointerDown} />

      {/* HUD */}
      {phase === 'playing' && (
        <>
          <div style={{ ...S.hudTop, ...(compact ? S.hudTopCompact : null) }}>
            <div style={{ ...S.stat, ...(compact ? S.statCompact : null) }}>
              <span style={S.statLabel}>Score</span>
              <span style={{ ...S.statVal, ...(compact ? S.statValCompact : null) }}>{score}</span>
            </div>
            <div style={{ ...S.target, ...(compact ? S.targetCompact : null) }} onClick={() => speakNumber(target)} title="Hear it again">
              <span style={S.targetLabel}>Catch number</span>
              <div style={S.targetRow}>
                <span style={{ ...S.targetNum, ...(compact ? S.targetNumCompact : null) }}>{target.n}</span>
                <div style={S.targetWordWrap}>
                  <span style={{ ...S.targetWord, ...(compact ? S.targetWordCompact : null) }}>{target.word}</span>
                  {!compact && <span style={S.targetPron}>{target.pron}</span>}
                </div>
                <span style={{ ...S.speaker, ...(compact ? { fontSize: 16, marginLeft: 0 } : null) }}>🔊</span>
              </div>
            </div>
            <div style={{ ...S.stat, alignItems: 'flex-end', ...(compact ? S.statCompact : null) }}>
              <span style={S.statLabel}>Streak</span>
              <span style={{ ...S.statVal, ...(compact ? S.statValCompact : null) }}>{streak > 1 ? `🔥${streak}` : streak}</span>
            </div>
          </div>

          <div style={{ ...S.hudBottom, ...(compact ? S.hudBottomCompact : null) }}>
            <div style={{ ...S.catchPill, ...(compact ? S.catchPillCompact : null) }}>🐟 {caught} / {goal}</div>
            <div style={S.timeWrap}>
              <div style={{ ...S.timeFill, width: `${timePct * 100}%`, background: timePct < 0.25 ? '#ff6b6b' : '#ffd24d' }} />
            </div>
            <span style={S.timeNum}>{timeLeft}s</span>
            <button style={{ ...S.iconBtn, ...(compact ? S.iconBtnCompact : null) }} onClick={() => setMuted((m) => !m)} aria-label="Mute">{muted ? '🔇' : '🔊'}</button>
            <button style={{ ...S.iconBtn, ...(compact ? S.iconBtnCompact : null) }} onClick={() => { finishRound(false); setPhase('menu'); }} aria-label="Menu">✕</button>
          </div>
        </>
      )}

      {/* Menu */}
      {phase === 'menu' && (
        <div style={S.overlay}>
          <div style={S.card}>
            <div style={S.titleAr}>بحر الأرقام</div>
            <h1 style={S.titleEn}>Number Fishing</h1>
            <p style={S.tagline}>Listen, read, and catch the fish wearing the right Arabic number.</p>
            {dataReady ? (
              <div style={S.levelGrid}>
                {Object.entries(LEVELS).map(([k, v]) => (
                  <button key={k} style={S.levelBtn} onClick={() => startLevel(Number(k))}>
                    <span style={S.levelTitle}>{v.title}</span>
                    <span style={S.levelSub}>{v.sub}</span>
                    <span style={S.levelMeta}>Catch {v.goal} · {v.time}s</span>
                  </button>
                ))}
              </div>
            ) : (
              <p style={{ ...S.hint, fontSize: 15 }}>Loading numbers…</p>
            )}
            <p style={S.hint}>Tap the speaker on the card any time to hear the number again.</p>
            <Link to="/" style={S.backLink}>← Back to games</Link>
          </div>
        </div>
      )}

      {/* Complete */}
      {phase === 'complete' && (
        <div style={S.overlay}>
          <div style={S.card}>
            <h2 style={S.doneTitle}>{result.stars >= 2 ? 'Great catch!' : 'Round over'}</h2>
            <Stars n={result.stars} />
            <div style={S.scoreRow}>
              <div style={S.scoreCell}><span style={S.scoreBig}>{score}</span><span style={S.scoreLbl}>Score</span></div>
              <div style={S.scoreCell}><span style={S.scoreBig}>{caught}</span><span style={S.scoreLbl}>Caught</span></div>
              <div style={S.scoreCell}><span style={S.scoreBig}>{result.accuracy}%</span><span style={S.scoreLbl}>Accuracy</span></div>
              <div style={S.scoreCell}><span style={S.scoreBig}>{bestStreak}</span><span style={S.scoreLbl}>Best streak</span></div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 18, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button style={S.primaryBtn} onClick={() => startLevel(level)}>Play again</button>
              {level < MAX_LEVEL && result.stars >= 2 && <button style={S.primaryBtn} onClick={() => startLevel(level + 1)}>Next level →</button>}
              <button style={S.ghostBtn} onClick={() => setPhase('menu')}>Levels</button>
            </div>
          </div>
        </div>
      )}

      {/* Rotate nudge — phones in portrait only; auto-hides on landscape */}
      {compact && portrait && !rotateDismissed && (
        <div style={S.rotateOverlay}>
          <div style={S.rotateCard}>
            <div style={S.rotateIcon}>📱</div>
            <p style={S.rotateText}>Number Fishing plays best in landscape — turn your device sideways to give the fish more room to swim.</p>
            <button style={S.rotateBtn} onClick={() => setRotateDismissed(true)}>Play in portrait</button>
          </div>
        </div>
      )}
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&family=Cairo:wght@600;700;800&display=swap');
* { box-sizing: border-box; }
@keyframes pop { 0%{transform:scale(.85);opacity:0} 100%{transform:scale(1);opacity:1} }
@keyframes floaty { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
`;

const FONT = "Fredoka, system-ui, -apple-system, sans-serif";
const AR = "Cairo, 'Segoe UI', Tahoma, sans-serif";

const S = {
  wrap: { position: 'relative', width: '100%', height: 'min(82vh, 720px)', minHeight: 460, borderRadius: 20, overflow: 'hidden', fontFamily: FONT, userSelect: 'none', WebkitTapHighlightColor: 'transparent', boxShadow: '0 18px 50px rgba(4,30,52,.35)' },
  canvas: { display: 'block', width: '100%', height: '100%', touchAction: 'none', cursor: 'pointer' },

  hudTop: { position: 'absolute', top: 0, left: 0, right: 0, padding: '12px 14px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, pointerEvents: 'none' },
  stat: { display: 'flex', flexDirection: 'column', pointerEvents: 'none', background: 'rgba(4,40,64,.34)', backdropFilter: 'blur(6px)', padding: '6px 12px', borderRadius: 12 },
  statLabel: { fontSize: 11, color: '#bfe9ff', letterSpacing: '.06em', textTransform: 'uppercase' },
  statVal: { fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.1 },

  target: { pointerEvents: 'auto', cursor: 'pointer', background: 'rgba(255,255,255,.95)', borderRadius: 16, padding: '8px 14px', boxShadow: '0 8px 22px rgba(4,30,52,.3)', animation: 'pop .35s ease', minWidth: 168 },
  targetLabel: { fontSize: 11, fontWeight: 600, color: '#0e7aa3', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', textAlign: 'center' },
  targetRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 },
  targetNum: { fontSize: 38, fontWeight: 700, color: '#0c3a4a', lineHeight: 1 },
  targetWordWrap: { display: 'flex', flexDirection: 'column', lineHeight: 1.05 },
  targetWord: { fontFamily: AR, fontSize: 22, fontWeight: 700, color: '#0c3a4a', direction: 'rtl' },
  targetPron: { fontSize: 12, color: '#5a7a88', fontStyle: 'italic' },
  speaker: { fontSize: 20, marginLeft: 2 },

  hudBottom: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 },
  catchPill: { background: 'rgba(4,40,64,.4)', color: '#fff', fontWeight: 600, fontSize: 15, padding: '6px 14px', borderRadius: 20, backdropFilter: 'blur(6px)' },
  timeWrap: { flex: 1, height: 10, background: 'rgba(4,40,64,.4)', borderRadius: 6, overflow: 'hidden' },
  timeFill: { height: '100%', borderRadius: 6, transition: 'width .25s linear' },
  timeNum: { color: '#fff', fontWeight: 600, fontSize: 14, minWidth: 34, textAlign: 'right' },
  iconBtn: { width: 38, height: 38, borderRadius: 12, border: 'none', background: 'rgba(4,40,64,.4)', color: '#fff', fontSize: 17, cursor: 'pointer', backdropFilter: 'blur(6px)' },

  overlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18, background: 'radial-gradient(circle at 50% 35%, rgba(8,70,110,.45), rgba(4,24,42,.78))' },
  card: { width: 'min(560px, 96%)', background: 'rgba(255,255,255,.97)', borderRadius: 24, padding: '26px 26px 22px', textAlign: 'center', boxShadow: '0 24px 70px rgba(4,20,40,.5)', animation: 'pop .35s ease' },
  titleAr: { fontFamily: AR, fontSize: 30, fontWeight: 800, color: '#0e7aa3', direction: 'rtl', animation: 'floaty 4s ease-in-out infinite' },
  titleEn: { margin: '2px 0 6px', fontSize: 38, fontWeight: 700, color: '#0c3a4a', letterSpacing: '-.01em' },
  tagline: { margin: '0 auto 18px', maxWidth: 380, fontSize: 15, color: '#4a6b78', lineHeight: 1.5 },
  levelGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 },
  levelBtn: { display: 'flex', flexDirection: 'column', gap: 3, padding: '14px 12px', borderRadius: 16, border: '2px solid #d6ecf3', background: 'linear-gradient(180deg,#f3fbfe,#e3f4fa)', cursor: 'pointer', transition: 'transform .12s, border-color .12s', fontFamily: FONT },
  levelTitle: { fontSize: 17, fontWeight: 700, color: '#0c3a4a' },
  levelSub: { fontSize: 13, color: '#3f7d96' },
  levelMeta: { fontSize: 12, color: '#7a96a2', marginTop: 2 },
  hint: { marginTop: 16, fontSize: 12.5, color: '#88a2ad' },
  backLink: { display: 'inline-block', marginTop: 14, color: '#0e7aa3', fontSize: 14, fontWeight: 600, textDecoration: 'none' },

  doneTitle: { margin: '0 0 14px', fontSize: 30, fontWeight: 700, color: '#0c3a4a' },
  scoreRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, margin: '20px 0 4px' },
  scoreCell: { display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 4px', background: '#eef8fc', borderRadius: 14 },
  scoreBig: { fontSize: 22, fontWeight: 700, color: '#0e7aa3' },
  scoreLbl: { fontSize: 11.5, color: '#6a8a96' },
  primaryBtn: { padding: '12px 22px', borderRadius: 14, border: 'none', background: 'linear-gradient(180deg,#ff8a5c,#ef6b3a)', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, boxShadow: '0 8px 20px rgba(239,107,58,.4)' },
  ghostBtn: { padding: '12px 22px', borderRadius: 14, border: '2px solid #cfe6ef', background: '#fff', color: '#0e7aa3', fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: FONT },

  // compact (phone) HUD overrides — top bar drops below the boat band
  hudTopCompact: { top: 64, padding: '0 8px', gap: 6, alignItems: 'center' },
  statCompact: { padding: '3px 8px', borderRadius: 9 },
  statValCompact: { fontSize: 15 },
  targetCompact: { padding: '5px 10px', minWidth: 0, borderRadius: 13 },
  targetNumCompact: { fontSize: 26 },
  targetWordCompact: { fontSize: 17 },
  hudBottomCompact: { padding: '8px', gap: 6 },
  catchPillCompact: { fontSize: 13, padding: '5px 10px' },
  iconBtnCompact: { width: 34, height: 34, fontSize: 15, borderRadius: 10 },

  rotateOverlay: { position: 'absolute', inset: 0, zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22, background: 'rgba(4,24,42,.84)' },
  rotateCard: { textAlign: 'center', maxWidth: 300 },
  rotateIcon: { fontSize: 56, animation: 'floaty 2.6s ease-in-out infinite' },
  rotateText: { color: '#eaf6fb', fontSize: 15.5, lineHeight: 1.55, margin: '14px 0 18px' },
  rotateBtn: { padding: '10px 20px', borderRadius: 12, border: '2px solid rgba(255,255,255,.4)', background: 'transparent', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT },
};